import { toNumber, toUtcDate } from '@rahtab/system/lib/tools/string';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { deserialize, serialize } from 'class-transformer';
import { SliceState, Statusable } from 'interfaces';
import { RootState } from 'rahtab-redux';
import type { OfferTemplateInfo, OrderingType } from '../../hooks/useMerchandises';
import type { Merchandise } from './merchandisesSlice';

const initialState: MerchandiseSliceState = {
  loading: 'idle',
  merchandiseOrderings: {},
};

const merchandiseOrderingSlice = createSlice({
  name: 'merchandiseOrdering',
  initialState,
  reducers: {
    setMerchandiseOrderingLoading(state, { payload }: PayloadAction<MerchandiseSliceState['loading']>) {
      state.loading = payload;
    },
    addMerchandiseOrdering(state, { payload }: PayloadAction<OfferTemplateInfo>) {
      const {
        TemplateCode: merchandiseId,
        Status: statusText,
        StatusCode: status,
        SupplierCount: sellerCount,
        TemplateDeliveryDate: initialDeliveryDate,
        OrderingType: orderingType,
      } = payload;
      const merchandise: MerchandiseOrdering = {
        initialDeliveryDate: <Date>(<unknown>serialize(toUtcDate(initialDeliveryDate, 'jYYYY/jMM/jDD'))),
        sellerCount: toNumber(sellerCount),
        status: status === 1 ? MerchandiseOrderingStatus.Active : MerchandiseOrderingStatus.Inactive,
        statusText,
        orderingType,
      };
      state.merchandiseOrderings[merchandiseId] = merchandise;
    },
  },
});

const { actions, reducer } = merchandiseOrderingSlice;

export default reducer;
export const { addMerchandiseOrdering, setMerchandiseOrderingLoading } = actions;

export const MerchandiseOrderingStatus = {
  Inactive: 0,
  Active: 1,
} as const;

export function orderingMerchandiseSelector(id: string): (state: RootState) => Merchandise & MerchandiseOrdering;
export function orderingMerchandiseSelector(
  ids: string[],
): (state: RootState) => Array<Merchandise & MerchandiseOrdering>;
export function orderingMerchandiseSelector(ids: string | string[]) {
  return (state: RootState) => {
    const {
      merchandise: { merchandises },
      merchandiseOrdering: { merchandiseOrderings },
    } = state;
    if (typeof ids === 'string') return getOrderingMerchandise(ids);
    return ids.map((id) => getOrderingMerchandise(id));
    function getOrderingMerchandise(id: string): Merchandise & MerchandiseOrdering {
      return {
        ...merchandises[id],
        ...merchandiseOrderings[id],
        initialDeliveryDate: deserialize(
          Date,
          (state.merchandiseOrdering.merchandiseOrderings[id].initialDeliveryDate as unknown) as string,
        ),
      };
    }
  };
}

export interface MerchandiseOrdering extends Statusable<typeof MerchandiseOrderingStatus> {
  sellerCount: number;
  initialDeliveryDate: Date;
  orderingType: OrderingType;
}

type MerchandiseSliceState = SliceState<'merchandiseOrderings', MerchandiseOrdering>;
